/*
 * MDJ integrations layer.
 *
 * Thin abstraction over outbound theme requests. Per the architecture
 * brief in CLAUDE.md: no fetch() in the theme has a hardcoded URL —
 * everything goes through this module, which reads endpoint URLs +
 * any signing secrets from theme settings (set on the global
 * window.__MDJ_INTEGRATIONS__ blob from layout/theme.liquid).
 *
 * Public surface (attached to window.MDJIntegrations):
 *   send(name, payload)
 *     Looks up the configured endpoint for `name` (e.g. "vault"),
 *     POSTs `payload` as JSON. Returns a Promise<boolean> indicating
 *     success. Silently no-ops + resolves false when the endpoint
 *     isn't configured, so callers don't need to feature-flag.
 *
 *   deviceId()
 *     Stable per-device identifier (UUID-ish), persisted in
 *     localStorage. Used as a fallback identifier when the visitor is
 *     anonymous.
 *
 *   shopContext()
 *     { shop, currency, customer } shape — what every webhook payload
 *     gets stamped with by `send`.
 */

(function () {
  const STORAGE_PREFIX = 'mdj.';
  const DEVICE_ID_KEY = STORAGE_PREFIX + 'device_id';

  function getConfig() {
    return (typeof window !== 'undefined' && window.__MDJ_INTEGRATIONS__) || {};
  }

  function safeStorage() {
    try {
      const k = '__mdj_storage_test__';
      window.localStorage.setItem(k, k);
      window.localStorage.removeItem(k);
      return window.localStorage;
    } catch (e) {
      return null;
    }
  }

  function ensureDeviceId() {
    const storage = safeStorage();
    if (!storage) return 'anon-no-storage';
    let id = storage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = 'mdj-' + (
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2) + Date.now().toString(36)
      );
      storage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  }

  function shopContext() {
    const cfg = getConfig();
    return {
      shop: cfg.shop || (typeof location !== 'undefined' ? location.hostname : ''),
      currency: cfg.currency || 'USD',
      customer: cfg.customer || null
    };
  }

  async function send(name, payload) {
    const cfg = getConfig();
    const endpoints = cfg.endpoints || {};
    const url = endpoints[name];
    if (!url) return false;

    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    const secrets = cfg.secrets || {};
    if (secrets[name]) headers['X-MDJ-' + name.replace(/[^a-z0-9]/gi, '-') + '-Secret'] = secrets[name];

    const body = JSON.stringify(Object.assign(
      {
        timestamp: new Date().toISOString(),
        device_id: ensureDeviceId()
      },
      shopContext(),
      payload || {}
    ));

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: body,
        keepalive: true
      });
      return !!(res && res.ok);
    } catch (err) {
      return false;
    }
  }

  /*
   * Memo-request intake — JewelersDigital `memo_request.v1`.
   *
   * The memo notification used to ride Shopify's native contact form,
   * which Shopify silently dropped (missing CAPTCHA token on the AJAX
   * POST). This sends the request straight to the JD intake endpoint
   * instead — no CAPTCHA, no spam filter, a real success/fail response.
   *
   * Contract (all read from theme settings/metafields — nothing here is
   * hardcoded, so the Smadify base fork inherits the seam cleanly):
   *   - URL    : endpoints.memo            (settings.memo_webhook_url)
   *   - Auth   : Authorization: Bearer …   (secrets.memo, from a shop
   *              metafield — a publishable intake key, origin-allowlisted
   *              by JD, scoped to memo_requests:create)
   *   - Idempotency-Key: the memo id, so a retried/resubmitted request
   *              dedupes at JD instead of double-booking.
   *
   * `memoRequest` is a stored MDJMemos entry { id, items[], note,
   * total_price (cents), currency, requestedAt }; `contact` is the
   * partner context resolved from the customer. Returns Promise<boolean>
   * — true only when JD accepted it (res.ok). Resolves false (no throw)
   * when the endpoint isn't configured, so callers can surface a
   * fallback without feature-flagging.
   */
  function buildMemoRequestV1(memoRequest, contact, cfg) {
    contact = contact || {};
    const addr = contact.address || {};
    const items = (memoRequest.items || []).map(function (it) {
      let url = it.url || '';
      if (url && url.slice(0, 4) !== 'http' && typeof location !== 'undefined') {
        url = location.origin + url;
      }
      return {
        title: it.title || '',
        sku: it.sku || '',
        quantity: it.qty || 1,
        line_price_cents: it.line_price || 0,
        variant_id: it.variant_id || it.id || null,
        url: url
      };
    });

    return {
      schema_version: 'memo_request.v1',
      event: 'memo.requested',
      occurred_at: memoRequest.requestedAt
        ? new Date(memoRequest.requestedAt).toISOString()
        : new Date().toISOString(),
      // Must be the .myshopify.com domain — shop.permanent_domain, not
      // the public domain (JD validator enforces this).
      source: { shop_domain: cfg.shop || '' },
      partner: {
        shopify_customer_id: contact.customer_id == null ? '' : String(contact.customer_id),
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        company: contact.company || '',
        address: {
          // Fold address2 into line1 — the v1 address has no line2 and
          // the validator is strict about shape.
          line1: [addr.address1, addr.address2].filter(Boolean).join(', '),
          city: addr.city || '',
          region: addr.province_code || addr.province || '',
          postal_code: addr.zip || '',
          country: addr.country_code || addr.country || ''
        }
      },
      memo: {
        id: String(memoRequest.id),
        currency: memoRequest.currency || cfg.currency || 'USD',
        total_price_cents: memoRequest.total_price || 0,
        note: memoRequest.note || '',
        items: items,
        // Purpose of the memo request (stable keys), trunk-show event
        // dates, and the 10% sell-through acknowledgment. Additive to
        // memo_request.v1 — JD ignores unknown fields until the intake is
        // updated to capture them. event_dates / sell_through_agreement are
        // null unless Trunk Show / Special Event was selected.
        purposes: (memoRequest.purposes || []).map(function (p) { return p && p.key ? p.key : p; }),
        purpose_labels: (memoRequest.purposes || []).map(function (p) { return (p && p.label) || ''; }),
        event_dates: memoRequest.eventDates || null,
        sell_through_agreement: memoRequest.sellThroughAgreement || null
      }
    };
  }

  async function sendMemo(memoRequest, contact) {
    const cfg = getConfig();
    const endpoints = cfg.endpoints || {};
    const url = endpoints.memo;
    if (!url) return false;
    if (!memoRequest || memoRequest.id == null) return false; // memo.id is required

    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Idempotency-Key': String(memoRequest.id)
    };
    const token = (cfg.secrets && cfg.secrets.memo) || '';
    if (token) headers['Authorization'] = 'Bearer ' + token;

    const body = JSON.stringify(buildMemoRequestV1(memoRequest, contact, cfg));

    try {
      const res = await fetch(url, { method: 'POST', headers: headers, body: body });
      return !!(res && res.ok);
    } catch (err) {
      return false;
    }
  }

  /*
   * Customer-facing SKU formatter. MDJ stores internal numbers as
   * "40XXXXX" (the "40" is an internal prefix) but speaks to clients
   * using the trailing digits alone. The prefix is configured via the
   * `sku_display_prefix` theme setting (exposed on
   * __MDJ_INTEGRATIONS__.sku_display_prefix from layout/theme.liquid).
   * When blank, this is a no-op — the SKU passes through unchanged.
   *
   * Used by every JS surface that renders a SKU from a dynamic source
   * (cart-confirm modal, memo modal items, account dashboard preview
   * cards, etc). Liquid-rendered SKUs are stripped at the resolution
   * point in their respective sections via the same prefix setting.
   */
  function formatSku(raw) {
    if (raw == null) return '';
    var s = String(raw);
    var cfg = getConfig();
    var prefix = cfg.sku_display_prefix || '';
    if (prefix && s.length > prefix.length && s.slice(0, prefix.length) === prefix) {
      return s.slice(prefix.length);
    }
    return s;
  }

  window.MDJIntegrations = { send, sendMemo, deviceId: ensureDeviceId, shopContext, formatSku };
  // Convenience global for inline-rendered components that don't pull
  // the full MDJIntegrations namespace.
  window.MDJFormatSku = formatSku;
})();
